// Copyright (c) 2026 Yunus YILDIZ — SPDX-License-Identifier: BUSL-1.1

import { useRef } from 'react'
import { Command } from 'cmdk'
import { useNavigate } from 'react-router-dom'
import { Search, Key, FileText, Webhook, ArrowRight } from 'lucide-react'
import { useApiKeyList } from '@/lib/hooks/useApiKeys'
import { useDocumentList } from '@/lib/hooks/useDocuments'
import { useWebhookList } from '@/lib/hooks/useWebhooks'
import { ROUTES } from '@/constants/routes'
import { SEARCH_INDEX } from '@/lib/search/searchIndex'
import type { SearchEntry } from '@/lib/search/searchIndex'

interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const GROUP_HEADING = '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-ink-subtle'
const ITEM = 'flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-ink aria-selected:bg-brand-subtle aria-selected:text-brand'

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const { data: apiKeys } = useApiKeyList()
  const { data: docs } = useDocumentList({ perPage: 50 })
  const { data: webhooks } = useWebhookList()

  function go(to: string, q?: string) {
    onOpenChange(false)
    navigate(q ? `${to}?q=${encodeURIComponent(q)}` : to)
  }

  function renderEntry(entry: SearchEntry) {
    return (
      <Command.Item
        key={entry.id}
        value={`${entry.text} ${entry.description ?? ''} ${(entry.keywords ?? []).join(' ')}`}
        onSelect={() => go(entry.to)}
        className={ITEM}
      >
        <entry.icon className="h-4 w-4 shrink-0 text-ink-muted" />
        <div className="flex-1 min-w-0">
          <span className="block">{entry.text}</span>
          {entry.description && (
            <span className="block text-xs text-ink-muted truncate">{entry.description}</span>
          )}
        </div>
        {entry.section && (
          <span className="shrink-0 text-[10px] text-ink-subtle">{entry.page} › {entry.section}</span>
        )}
        <ArrowRight className="h-3.5 w-3.5 shrink-0 opacity-0 aria-selected:opacity-100" />
      </Command.Item>
    )
  }

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
          onClick={() => onOpenChange(false)}
        />
      )}

      {open && (
        <div className="fixed left-1/2 top-[18vh] z-50 w-full max-w-xl -translate-x-1/2 px-4 animate-slide-up">
          <Command
            className="overflow-hidden rounded-xl border border-line bg-raised shadow-2xl"
            loop
          >
            {/* Search input */}
            <div className="flex items-center gap-2 border-b border-line px-4">
              <Search className="h-4 w-4 shrink-0 text-ink-muted" />
              <Command.Input
                ref={inputRef}
                placeholder="Search anything…"
                className="h-12 w-full bg-transparent text-sm text-ink placeholder:text-ink-subtle appearance-none outline-none"
                autoFocus
              />
              <kbd className="hidden shrink-0 rounded border border-line px-1.5 py-0.5 font-mono text-[10px] text-ink-muted sm:block">
                ESC
              </kbd>
            </div>

            <Command.List className="max-h-[420px] overflow-y-auto p-2">
              <Command.Empty className="py-10 text-center text-sm text-ink-muted">
                No results found.
              </Command.Empty>

              {/* Static index — all pages & sections */}
              <Command.Group heading="Content" className={GROUP_HEADING}>
                {SEARCH_INDEX.map(renderEntry)}
              </Command.Group>

              {/* Dynamic — API Keys */}
              {apiKeys && apiKeys.length > 0 && (
                <Command.Group heading="API Keys" className={GROUP_HEADING}>
                  {apiKeys.map((key) => (
                    <Command.Item
                      key={key.id}
                      value={`api key ${key.name} ${key.prefix} ${key.scopes.join(' ')}`}
                      onSelect={() => go(ROUTES.API_KEYS, key.name)}
                      className={ITEM}
                    >
                      <Key className="h-4 w-4 shrink-0 text-ink-muted" />
                      <div className="flex-1 min-w-0">
                        <span className="block">{key.name}</span>
                        <span className="block text-xs text-ink-muted">{key.scopes.join(', ')}</span>
                      </div>
                      <span className="shrink-0 font-mono text-xs text-ink-subtle">{key.prefix}…</span>
                    </Command.Item>
                  ))}
                </Command.Group>
              )}

              {/* Dynamic — Documents */}
              {docs && docs.data.length > 0 && (
                <Command.Group heading="Documents" className={GROUP_HEADING}>
                  {docs.data.map((doc) => (
                    <Command.Item
                      key={doc.id}
                      value={`document file ${doc.filename} ${doc.status}`}
                      onSelect={() => go(`${ROUTES.DOCUMENTS}/${doc.id}`)}
                      className={ITEM}
                    >
                      <FileText className="h-4 w-4 shrink-0 text-ink-muted" />
                      <div className="flex-1 min-w-0">
                        <span className="block truncate">{doc.filename}</span>
                        <span className="block text-xs text-ink-muted">{doc.status}</span>
                      </div>
                    </Command.Item>
                  ))}
                </Command.Group>
              )}

              {/* Dynamic — Webhooks */}
              {webhooks && webhooks.length > 0 && (
                <Command.Group heading="Webhooks" className={GROUP_HEADING}>
                  {webhooks.map((wh) => (
                    <Command.Item
                      key={wh.id}
                      value={`webhook endpoint ${wh.url} ${wh.description ?? ''} ${wh.events.join(' ')}`}
                      onSelect={() => go(ROUTES.WEBHOOKS, wh.url)}
                      className={ITEM}
                    >
                      <Webhook className="h-4 w-4 shrink-0 text-ink-muted" />
                      <div className="flex-1 min-w-0">
                        <span className="block truncate font-mono text-xs">{wh.url}</span>
                        {wh.description && (
                          <span className="block text-xs text-ink-muted">{wh.description}</span>
                        )}
                      </div>
                    </Command.Item>
                  ))}
                </Command.Group>
              )}
            </Command.List>

            {/* Footer hint */}
            <div className="flex items-center gap-3 border-t border-line px-4 py-2 text-[10px] text-ink-subtle">
              <span><kbd className="font-mono">↑↓</kbd> navigate</span>
              <span><kbd className="font-mono">↵</kbd> open</span>
              <span><kbd className="font-mono">ESC</kbd> close</span>
            </div>
          </Command>
        </div>
      )}
    </>
  )
}
